// Global WhatsApp click handler: any click on a link/button targeting wa.me /
// whatsapp is intercepted. Before opening WhatsApp we ask for name + phone in a
// small modal, so every lead in /admin/leads is identified. The lead is stored
// via the whatsapp-lead edge function, then WhatsApp opens as usual.
// Links/forms that already captured the contact upstream can opt out with the
// `data-wa-skip-capture` attribute (they still get fire-and-forget logging).
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { handleWhatsAppClick } from "@/lib/analytics";

type PopupEvent = "opened" | "submitted" | "skipped" | "abandoned" | "blocked";

// Funnel tracking for the capture pop-up (fire-and-forget, never blocks the visitor).
const trackPopup = (event: PopupEvent, source: string) => {
  try {
    const seg = window.location.pathname.split("/")[1];
    void supabase.from("wa_popup_events").insert({
      event,
      source: source.slice(0, 100),
      page_path: window.location.pathname.slice(0, 500),
      language: ["de", "fr", "it", "es", "pt"].includes(seg) ? seg : "en",
    }).then(() => undefined, () => undefined);
  } catch { /* ignore */ }
};

const COPY: Record<string, {
  title: string;
  sub: string;
  name: string;
  phone: string;
  email: string;
  submit: string;
  sending: string;
  skip: string;
  nameErr: string;
  phoneErr: string;
  emailErr: string;
}> = {
  en: {
    title: "Almost there!",
    sub: "Tell us who you are so we can reply to you personally on WhatsApp.",
    name: "Your name",
    phone: "Phone (with country code)",
    email: "Email (optional)",
    submit: "Continue to WhatsApp",
    sending: "Opening…",
    skip: "Continue without sharing",
    nameErr: "Please enter your name",
    phoneErr: "Please enter a valid phone number",
    emailErr: "Please enter a valid email address",
  },
  it: {
    title: "Quasi fatto!",
    sub: "Dicci chi sei così possiamo risponderti personalmente su WhatsApp.",
    name: "Il tuo nome",
    phone: "Telefono (con prefisso)",
    email: "Email (opzionale)",
    submit: "Continua su WhatsApp",
    sending: "Apertura…",
    skip: "Continua senza condividere",
    nameErr: "Inserisci il tuo nome",
    phoneErr: "Inserisci un numero di telefono valido",
    emailErr: "Inserisci un indirizzo email valido",
  },
  pt: {
    title: "Quase lá!",
    sub: "Diga-nos quem é para podermos responder pessoalmente no WhatsApp.",
    name: "O seu nome",
    phone: "Telefone (com indicativo)",
    email: "Email (opcional)",
    submit: "Continuar para o WhatsApp",
    sending: "A abrir…",
    skip: "Continuar sem partilhar",
    nameErr: "Indique o seu nome",
    phoneErr: "Indique um número válido",
    emailErr: "Indique um email válido",
  },
  de: {
    title: "Fast geschafft!",
    sub: "Sag uns, wer du bist, damit wir dir persönlich auf WhatsApp antworten können.",
    name: "Dein Name",
    phone: "Telefon (mit Vorwahl)",
    email: "E-Mail (optional)",
    submit: "Weiter zu WhatsApp",
    sending: "Öffnen…",
    skip: "Ohne Angaben fortfahren",
    nameErr: "Bitte Namen eingeben",
    phoneErr: "Bitte gültige Telefonnummer eingeben",
    emailErr: "Bitte gültige E-Mail-Adresse eingeben",
  },
  fr: {
    title: "Presque fini !",
    sub: "Dites-nous qui vous êtes pour que nous puissions vous répondre personnellement sur WhatsApp.",
    name: "Votre nom",
    phone: "Téléphone (avec indicatif)",
    email: "Email (optionnel)",
    submit: "Continuer sur WhatsApp",
    sending: "Ouverture…",
    skip: "Continuer sans partager",
    nameErr: "Indiquez votre nom",
    phoneErr: "Indiquez un numéro valide",
    emailErr: "Indiquez une adresse email valide",
  },
  es: {
    title: "¡Casi listo!",
    sub: "Dinos quién eres para poderte responder personalmente en WhatsApp.",
    name: "Tu nombre",
    phone: "Teléfono (con prefijo)",
    email: "Email (opcional)",
    submit: "Continuar a WhatsApp",
    sending: "Abriendo…",
    skip: "Continuar sin compartir",
    nameErr: "Indica tu nombre",
    phoneErr: "Indica un número válido",
    emailErr: "Indica un email válido",
  },
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PendingClick {
  href: string;
  message?: string;
  source: string;
}

const WhatsAppLeadTracker = () => {
  const location = useLocation();
  const [pending, setPending] = useState<PendingClick | null>(null);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const pendingRef = useRef<PendingClick | null>(null);
  pendingRef.current = pending;

  const language = (() => {
    const seg = location.pathname.split("/")[1];
    return ["de", "fr", "it", "es", "pt"].includes(seg) ? seg : "en";
  })();
  const t = COPY[language] || COPY.en;

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const link = target.closest("a") as HTMLAnchorElement | null;
      if (!link) return;
      const href = link.href || "";
      if (!/wa\.me|api\.whatsapp\.com|whatsapp:/i.test(href)) return;

      // Extract pre-filled message
      let message: string | undefined;
      try {
        const u = new URL(href);
        message = u.searchParams.get("text") ?? undefined;
      } catch { /* ignore */ }

      const lang = (() => {
        const seg = window.location.pathname.split("/")[1];
        return ["de", "fr", "it", "es", "pt"].includes(seg) ? seg : "en";
      })();

      const source =
        link.dataset.waSource ||
        link.closest("[data-wa-section]")?.getAttribute("data-wa-section") ||
        link.getAttribute("aria-label") ||
        "unknown";

      // GA4 generate_lead + (optional) Google Ads conversion.
      try { handleWhatsAppClick(source.slice(0, 100)); } catch { /* ignore */ }

      // Links that already captured the contact upstream opt out of the modal
      // and keep the old fire-and-forget logging.
      if (link.hasAttribute("data-wa-skip-capture")) {
        supabase.functions.invoke("whatsapp-lead", {
          body: {
            pagePath: window.location.pathname,
            source: source.slice(0, 100),
            message,
            language: lang,
            context: { href: href.slice(0, 500) },
          },
        }).catch(() => { /* swallow */ });
        return;
      }

      // Intercept: ask who they are first, then open WhatsApp.
      e.preventDefault();
      setErr(null);
      setPending({ href, message, source: source.slice(0, 100) });
      trackPopup("opened", source.slice(0, 100));
    };

    document.addEventListener("click", handler, true);
    return () => document.removeEventListener("click", handler, true);
  }, []);

  const openWhatsApp = (href: string) => {
    window.open(href, "_blank");
  };

  const resetFields = () => {
    setPending(null);
    setName("");
    setPhone("");
    setEmail("");
    setErr(null);
    setBusy(false);
  };

  // Closing without submitting or skipping = lost lead.
  const closeAndReset = () => {
    const click = pendingRef.current;
    if (click) trackPopup("abandoned", click.source);
    resetFields();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    const cleanName = name.trim();
    const cleanPhone = phone.replace(/[^\d+]/g, "");
    const cleanEmail = email.trim();
    if (!cleanName) { setErr(t.nameErr); return; }
    if (cleanPhone.length < 7) { setErr(t.phoneErr); return; }
    if (cleanEmail && !EMAIL_RE.test(cleanEmail)) { setErr(t.emailErr); return; }

    const click = pendingRef.current;
    if (!click) return;
    setBusy(true);

    // Open the tab synchronously (user gesture) so popup blockers don't kill
    // it after the async lead logging below.
    const win = window.open("", "_blank");

    const personalised = `${click.message || ""}\n\n${cleanName}\n📱 ${cleanPhone}${cleanEmail ? `\n✉ ${cleanEmail}` : ""}`.trim();

    try {
      await supabase.functions.invoke("whatsapp-lead", {
        body: {
          pagePath: location.pathname,
          source: click.source,
          message: personalised,
          language,
          name: cleanName,
          phone: cleanPhone,
          email: cleanEmail || null,
          context: { href: click.href.slice(0, 500), captured_via: "global_wa_intercept" },
        },
      });
    } catch { /* still open WhatsApp even if logging fails */ }

    // Append identity to the WhatsApp message so the team sees it in chat too.
    let href = click.href;
    try {
      const u = new URL(click.href);
      u.searchParams.set("text", personalised);
      href = u.toString();
    } catch { /* keep original */ }

    trackPopup("submitted", click.source);
    let opened: Window | null = win;
    if (win) {
      win.location.href = href;
    } else {
      opened = window.open(href, "_blank");
    }
    if (!opened) trackPopup("blocked", click.source);
    resetFields();
  };

  const skipCapture = () => {
    const click = pendingRef.current;
    if (!click) return;
    trackPopup("skipped", click.source);
    supabase.functions.invoke("whatsapp-lead", {
      body: {
        pagePath: location.pathname,
        source: click.source,
        message: click.message,
        language,
        context: { href: click.href.slice(0, 500), skipped_capture: true },
      },
    }).catch(() => { /* swallow */ });
    openWhatsApp(click.href);
    resetFields();
  };

  if (!pending) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) closeAndReset(); }}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16, zIndex: 9999,
      }}
    >
      <form
        onSubmit={submit}
        style={{
          background: "#fff", borderRadius: 16, padding: 24,
          width: "100%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
          color: "#111", fontFamily: "inherit",
        }}
      >
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{t.title}</h3>
        <p style={{ margin: "6px 0 16px", fontSize: 13, color: "#555", lineHeight: 1.4 }}>{t.sub}</p>

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.name} *</label>
        <input
          type="text" value={name} onChange={(e) => setName(e.target.value)}
          autoFocus required maxLength={80} autoComplete="name"
          style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 10 }}
        />

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.phone} *</label>
        <input
          type="tel" value={phone} onChange={(e) => setPhone(e.target.value)}
          required inputMode="tel" placeholder="+39 333 123 4567" maxLength={30} autoComplete="tel"
          style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 10 }}
        />

        <label style={{ display: "block", fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{t.email}</label>
        <input
          type="email" value={email} onChange={(e) => setEmail(e.target.value)}
          maxLength={120} inputMode="email" autoComplete="email"
          style={{ width: "100%", padding: "10px 12px", border: "1.5px solid #ddd", borderRadius: 8, fontSize: 15, marginBottom: 12 }}
        />

        {err && <p style={{ color: "#c00", fontSize: 13, margin: "4px 0 10px" }}>{err}</p>}

        <button
          type="submit" disabled={busy}
          style={{
            width: "100%", padding: "12px 16px", borderRadius: 10,
            background: "#25D366", color: "#fff", border: "none",
            fontWeight: 700, fontSize: 15, cursor: busy ? "wait" : "pointer",
            opacity: busy ? 0.7 : 1, marginBottom: 6,
          }}
        >
          {busy ? t.sending : t.submit}
        </button>
        <button
          type="button" onClick={skipCapture} disabled={busy}
          style={{
            width: "100%", padding: "10px", background: "transparent",
            border: "none", color: "#666", fontSize: 13, cursor: "pointer",
            textDecoration: "underline",
          }}
        >
          {t.skip}
        </button>
      </form>
    </div>
  );
};

export default WhatsAppLeadTracker;
